import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Bug, Plus, AlertCircle, CheckCircle2, Clock, X, Loader2, MessageSquare, IndianRupee } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useCustomerAuth, formatApiError } from '../contexts/CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customers`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const STATUS_META = {
  open: { cls: 'bg-amber-100 text-amber-800', label: '🟡 Open' },
  assigned: { cls: 'bg-indigo-100 text-indigo-700', label: '🔵 Assigned to Technician' },
  in_progress: { cls: 'bg-blue-100 text-blue-700', label: '🔵 In Progress' },
  awaiting_payment: { cls: 'bg-orange-100 text-orange-700', label: '🟠 Awaiting Payment' },
  resolved: { cls: 'bg-emerald-100 text-emerald-700', label: '🟢 Resolved' },
  force_closed: { cls: 'bg-slate-200 text-slate-700', label: '⚫ Closed' },
};

export default function CustomerServiceTicketsTab() {
  const { token } = useCustomerAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/me/service-tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data?.items || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="cust-service-tickets">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">My Service Tickets</h2>
          <p className="text-xs text-slate-500">Raise a complaint against any past service. We&apos;ll dispatch a technician to fix it.</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-amber-700 hover:bg-amber-800" size="sm" data-testid="cust-tkt-new"><Plus size={14} className="mr-1" /> Raise Ticket</Button>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}

      {loading && <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-center py-10 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">No tickets yet. Raise one if any pest comes back.</div>}

      <div className="space-y-2">
        {rows.map((t) => {
          const meta = STATUS_META[t.status] || STATUS_META.open;
          const lastHist = (t.status_history || [])[t.status_history?.length - 1];
          return (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`cust-tkt-${t.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold text-slate-700">{t.ticket_no}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                    {t.is_warranty_claim && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">FREE — Warranty</span>}
                  </div>
                  <div className="text-sm font-semibold mt-1"><Bug size={12} className="inline mr-1" /> {t.pest_label || t.pest_type}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{t.description}</div>
                  <div className="text-[11px] text-slate-500 mt-1">🧾 {t.booking_code} · Raised {fmtDate(t.created_at)}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] uppercase text-slate-500 font-bold">{t.charge_amount > 0 ? 'Service Charge' : 'FREE'}</div>
                  <div className="text-lg font-bold text-emerald-700">{fmtMoney(t.charge_amount)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">{t.charge_status}</div>
                </div>
              </div>

              {/* Resolution note (if resolved) */}
              {t.resolution_notes && (
                <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs" data-testid={`cust-tkt-resolution-${t.id}`}>
                  <div className="font-bold uppercase text-[10px] text-emerald-800 mb-0.5">✅ Resolved by Technician</div>
                  <div className="text-slate-700">{t.resolution_notes}</div>
                </div>
              )}

              {/* Force-close reason (if any) */}
              {t.status === 'force_closed' && t.force_closed_reason && (
                <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs" data-testid={`cust-tkt-closereason-${t.id}`}>
                  <div className="font-bold uppercase text-[10px] text-slate-700 mb-0.5">⚫ Ticket Closed by Admin</div>
                  <div className="text-slate-600">{t.force_closed_reason}</div>
                </div>
              )}

              {/* Assigned tech */}
              {t.assigned_to_name && t.status !== 'force_closed' && t.status !== 'resolved' && (
                <div className="mt-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1 inline-block">
                  👷 Assigned to <b>{t.assigned_to_name}</b>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showNew && <NewTicketModal token={token} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewTicketModal({ token, onClose, onCreated }) {
  const [bookings, setBookings] = useState([]);
  const [bookingId, setBookingId] = useState('');
  const [pestType, setPestType] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Customer bookings endpoint may return `{items:[]}`, plain `[]`, or `{bookings:[]}` —
    // be tolerant of all three shapes so the dropdown always populates.
    axios.get(`${API}/me/bookings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const data = r.data;
        const list = Array.isArray(data)
          ? data
          : (data?.items || data?.bookings || []);
        // Allow ANY past booking (customer can complain about any service they had).
        // Only hide cancelled / refunded ones.
        const usable = list.filter(b => !['cancelled', 'refunded'].includes(b.status));
        setBookings(usable);
      })
      .catch(() => setBookings([]));
  }, [token]);

  const selectedBooking = bookings.find(b => b.id === bookingId);

  const submit = async (e) => {
    e.preventDefault();
    if (!bookingId || !pestType || !description.trim()) { setErr('All fields are required'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/me/service-tickets`,
        { booking_id: bookingId, pest_type: pestType, pest_label: pestType, description: description.trim(), priority },
        { headers: { Authorization: `Bearer ${token}` } });
      onCreated();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const [manualMode, setManualMode] = useState(false);
  const [manualCode, setManualCode] = useState('');

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="cust-tkt-new-modal">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-md rounded-2xl overflow-hidden">
        <div className="px-5 py-3.5 bg-amber-700 text-white flex items-center justify-between">
          <div><div className="text-xs opacity-90">Raise a Complaint</div><div className="font-bold">Service Ticket</div></div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <Label className="text-xs">Which Booking? *</Label>
            {!manualMode ? (
              <>
                <select value={bookingId} onChange={(e) => { setBookingId(e.target.value); const b = bookings.find(x => x.id === e.target.value); if (b?.service_label) setPestType(b.service_label); }} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid="cust-tkt-booking">
                  <option value="">{bookings.length === 0 ? '— No past bookings found —' : '— Pick from your bookings —'}</option>
                  {bookings.map(b => <option key={b.id} value={b.id}>{b.booking_code} · {b.service_label || b.pest_type || 'Service'} · {fmtDate(b.scheduled_date)}</option>)}
                </select>
                <button type="button" onClick={() => setManualMode(true)} className="mt-1 text-[11px] text-amber-700 underline hover:text-amber-900" data-testid="cust-tkt-manual">
                  {bookings.length === 0 ? "Don't see your booking? Click here to enter the code manually" : "Type booking code manually"}
                </button>
              </>
            ) : (
              <>
                <Input
                  value={manualCode}
                  onChange={(e) => { setManualCode(e.target.value); setBookingId(e.target.value.trim()); }}
                  placeholder="e.g., BK260624002 (find this on your invoice / WhatsApp confirmation)"
                  className="mt-1 h-10 text-sm uppercase tracking-wide"
                  data-testid="cust-tkt-manual-code"
                />
                <button type="button" onClick={() => { setManualMode(false); setManualCode(''); setBookingId(''); }} className="mt-1 text-[11px] text-slate-500 underline hover:text-slate-700">
                  Switch back to dropdown
                </button>
              </>
            )}
          </div>
          <div>
            <Label className="text-xs">Which Pest is back? *</Label>
            <Input value={pestType} onChange={(e) => setPestType(e.target.value)} placeholder="Cockroach / Termite / etc." className="mt-1 h-10" data-testid="cust-tkt-pest" required />
            {selectedBooking?.is_warranty_claim_eligible && <div className="text-[11px] text-emerald-700 mt-1">✅ This booking is within warranty — likely FREE service.</div>}
          </div>
          <div>
            <Label className="text-xs">Describe the Issue *</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., I am seeing cockroaches again in the kitchen and behind the fridge after the last treatment 5 days ago." className="mt-1" data-testid="cust-tkt-desc" required />
          </div>
          <div>
            <Label className="text-xs">Priority</Label>
            <div className="flex gap-1.5 mt-1">
              {[['low', 'Low'], ['medium', 'Medium'], ['high', 'High']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setPriority(k)} className={`flex-1 px-2 py-1.5 rounded-md text-xs font-semibold ${priority === k ? (k === 'high' ? 'bg-rose-600 text-white' : k === 'medium' ? 'bg-amber-600 text-white' : 'bg-slate-600 text-white') : 'bg-slate-100 text-slate-600'}`} data-testid={`cust-tkt-pri-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded p-2 flex items-center gap-2"><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-amber-700 hover:bg-amber-800" data-testid="cust-tkt-submit">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null} Raise Ticket</Button>
        </div>
      </form>
    </div>
  );
}
